/**
 * `useLaminates` — live read of every laminate for a project,
 * grouped by room.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/laminates/useLaminates.ts
 *
 * Returns:
 *   - `data` — flat array sorted by (roomName asc, createdAt desc)
 *   - `rooms` — same data grouped into `{ roomName, laminates[] }[]`
 *   - `roomNames` — distinct room names
 *   - `brands` — distinct brand names (for the right-rail KPI)
 *   - `canEdit` — role gate; mirrors mobile's `laminate.write`
 *     capability (org owner or role in
 *     {superAdmin, admin, manager, siteEngineer, designer})
 *
 * Sort order matches mobile so room headers + items appear in the
 * same order on both clients.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { canEditTasks, type RoleKey } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';
import type { Laminate, RoomLaminates } from '@/lib/laminates';
import { useFullOrganization } from '@/lib/useFullOrganization';

export type UseLaminatesResult = {
  data: Laminate[];
  rooms: RoomLaminates[];
  roomNames: string[];
  brands: string[];
  loading: boolean;
  canEdit: boolean;
};

export function useLaminates(
  projectId: string | undefined,
  orgId: string | undefined,
): UseLaminatesResult {
  const { user } = useAuth();
  const { org } = useFullOrganization();
  const [data, setData] = useState<Laminate[]>([]);
  const [loading, setLoading] = useState(true);

  // Role-based edit capability — drives FAB visibility + form
  // mount. Server-side Firestore rule still gates writes regardless;
  // this is just the UI hide so clients don't see a button that
  // would deterministically fail.
  const canEdit = useMemo(() => {
    if (!user || !org) return false;
    const orgData = org.data as {
      ownerId?: string;
      roles?: Record<string, string>;
    };
    if (orgData.ownerId === user.uid) return true;
    const role = orgData.roles?.[user.uid] as RoleKey | undefined;
    return canEditTasks(role);
  }, [user, org]);

  useEffect(() => {
    if (!projectId || !orgId) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'laminates'),
      where('orgId', '==', orgId),
      where('projectId', '==', projectId),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: Laminate[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Laminate, 'id'>),
        }));
        // Same sort key as mobile: room name ASC, then most-recently-
        // added first within each room so a freshly-added laminate
        // shows at the top of its room block.
        rows.sort((a, b) => {
          const cmp = a.roomName.localeCompare(b.roomName);
          if (cmp !== 0) return cmp;
          const at = a.createdAt ? a.createdAt.toMillis() : 0;
          const bt = b.createdAt ? b.createdAt.toMillis() : 0;
          return bt - at;
        });
        setData(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useLaminates] snapshot error:', err);
        setLoading(false);
      },
    );
    return unsub;
  }, [projectId, orgId]);

  const rooms = useMemo<RoomLaminates[]>(() => {
    const map = new Map<string, Laminate[]>();
    for (const l of data) {
      const arr = map.get(l.roomName) ?? [];
      arr.push(l);
      map.set(l.roomName, arr);
    }
    return Array.from(map.entries()).map(([roomName, laminates]) => ({
      roomName,
      laminates,
    }));
  }, [data]);

  const roomNames = useMemo(() => rooms.map((r) => r.roomName), [rooms]);

  const brands = useMemo(() => {
    const set = new Set<string>();
    for (const l of data) if (l.brand) set.add(l.brand);
    return Array.from(set).sort();
  }, [data]);

  return { data, rooms, roomNames, brands, loading, canEdit };
}
